'use client';

import React, { useEffect, useState } from 'react';
import { ParsedProductVariant } from '@/ui/components/pages/ProductDetail/types/ParsedProductVariant';
import { SelectedProductVariants } from '@/ui/components/pages/ProductDetail/types/SelectedProductVariants';
import { useBundleVariantSelection } from '@/ui/hooks/productDetail/use-bundle-variant-selection';
import {
  useExternalAreaLink,
  useExternalAreaLinkDismissed,
} from '@/ui/providers/externalAreaLinkProvider';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { Bundle } from '@/ui/services/BundleService';
import { useCartStore } from '@/ui/store/cart';
import { pushBundleViewItem } from '@/utils/bundle-helper';
import { CLASSIC_PDP_MIN_QUANTITY } from '../use-classic-pdp-selection';

export interface ClassicBundleSelectionState {
  // The chosen variant per slot; the domain resolves a missing or foreign one to the default.
  selected: SelectedProductVariants;
  // The selected SKUs joined, which changes exactly when the purchase changes.
  selectionKey: string;
  selectVariant: (bundleProductInstanceId: string, variant: ParsedProductVariant) => void;
  quantity: number;
  setQuantity: (quantity: number) => void;
  finalPrice: number;
  originalPrice: number;
  saving: number;
  isAvailable: boolean;
  isShippingModalOpen: boolean;
  externalAreaLink: string | null;
  addToCart: () => void;
  notifyMe: () => void;
  closeShippingModal: () => void;
  proceedDespiteShippingWarning: () => void;
}

/**
 * Owns every stateful concern of the bundle purchase panel: the variant of each
 * slot, quantity, selection-aware prices, availability and the add-to-cart /
 * notify-me actions. It mirrors the product panel's hook so both arms behave
 * the same around stock and the external area shop.
 */
export function useClassicBundleSelection(bundle: Bundle): ClassicBundleSelectionState {
  const cartStore = useCartStore();
  const { country } = useLocalization();
  const externalAreaLink = useExternalAreaLink();
  const { isDismissed, dismiss } = useExternalAreaLinkDismissed();
  const { selectedVariants, selectVariant, resetForBundle } = useBundleVariantSelection(bundle);

  // The quantity belongs to the selection it was set for, like the remounted stepper.
  const [quantityState, setQuantityState] = useState({
    selectionKey: '',
    quantity: CLASSIC_PDP_MIN_QUANTITY,
  });
  const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);
  const viewedBundleIdRef = React.useRef<string | null>(null);

  // The view is tracked once per bundle, not on every re-created instance.
  useEffect(() => {
    resetForBundle(bundle);

    if (viewedBundleIdRef.current !== bundle.id) {
      viewedBundleIdRef.current = bundle.id;
      pushBundleViewItem(bundle);
    }
  }, [bundle, resetForBundle]);

  const selectionKey = bundle.getSelectedSkus(selectedVariants).join(',');

  // A different set of SKUs is a different purchase, so the amount starts over in the
  // same render instead of an effect a frame later.
  const quantity =
    quantityState.selectionKey === selectionKey ? quantityState.quantity : CLASSIC_PDP_MIN_QUANTITY;
  const setQuantity = (nextQuantity: number) =>
    setQuantityState({ selectionKey, quantity: nextQuantity });

  const finalPrice = bundle.getFinalPrice(selectedVariants);
  const originalPrice = bundle.getTotalOriginalPrice(selectedVariants);
  const isSelectionRegionRestricted = bundle.isSelectionRestrictedInCountry(
    selectedVariants,
    country,
  );
  const isInStock = bundle.isBundleVariantInStock(selectedVariants);
  const isAvailable = (isInStock || !!externalAreaLink) && !isSelectionRegionRestricted;
  const effectiveExternalAreaLink = isDismissed ? null : externalAreaLink;

  const addToCart = () => {
    // An external area link means this area is served by another shop, so the
    // visitor confirms in the modal before the set lands in this cart.
    if (effectiveExternalAreaLink) {
      setIsShippingModalOpen(true);
      return;
    }

    bundle.checkStockAndAddToCart(selectedVariants, cartStore, quantity);
  };

  const notifyMe = () => cartStore.setShowNotifyMe(true, null, bundle);

  const proceedDespiteShippingWarning = () => {
    setIsShippingModalOpen(false);
    dismiss();
  };

  const closeShippingModal = () => setIsShippingModalOpen(false);

  return {
    selected: selectedVariants,
    selectionKey,
    selectVariant,
    quantity,
    setQuantity,
    finalPrice,
    originalPrice,
    saving: originalPrice - finalPrice,
    isAvailable,
    isShippingModalOpen,
    externalAreaLink,
    addToCart,
    notifyMe,
    closeShippingModal,
    proceedDespiteShippingWarning,
  };
}
